import { computed } from "vue";
import {
  getCasingInteriorDiameter,
  getEquipmentCrossSectionFt2
} from "@/libs/calculations/pressureDropCorrelations";
import { extrapolateTVDByMD } from "@/libs/calculations/deviationSurvey";
import { Inches } from "@/libs/units/length";
import { calculateMotorCrossSectionFt2 } from "@/utils/equipmentTubingHelpers";
import type { DiametersAndWeightsCtx } from "./context";

// Tubing/casing/shroud diameters, cross sections and string weights.
export const defineDiametersAndWeights = (ctx: DiametersAndWeightsCtx) => {
  const {
    _equipment,
    _wellProperties,
    orderedYToolBypassTubings,
    orderedYToolEquipmentTubings,
    orderedIntakes,
    descendingOrderPumps,
    descendingOrderSeals,
    primarySeal,
    bottomSeal,
    bottomIntake,
    tubingLengthFt,
    yToolLengthFt,
    bottomOfIntakesDepthFt,
    bottomOfMotorDepthFt,
    bottomOfEquipmentDepthFt
  } = ctx;

  const casingInteriorDiameterAtIntakeIn = computed<number>(() =>
    getCasingInteriorDiameter(
      _wellProperties.value,
      bottomOfIntakesDepthFt.value
    )
  );

  const casingInteriorDiameterAtSensorIn = computed<number>(() =>
    getCasingInteriorDiameter(
      _wellProperties.value,
      bottomOfEquipmentDepthFt.value
    )
  );

  const bottomCasingInteriorDiameterIn = computed<number>(() => {
    return ctx.basicData.value.bottomCasingLengthFt &&
      ctx.basicData.value.bottomCasingInteriorDiameterIn
      ? ctx.basicData.value.bottomCasingInteriorDiameterIn
      : ctx.basicData.value.topCasingInteriorDiameterIn;
  });

  // If there is a y tool then the tubing at pump discharge will be the equipment
  // tubing attached to it
  const tubingInternalDiameterAtPumpDischargeIn = computed(() => {
    if (
      _equipment.value?.use_uphole_y_tool &&
      orderedYToolEquipmentTubings.value.length > 0
    ) {
      return (
        orderedYToolEquipmentTubings.value[
          orderedYToolEquipmentTubings.value.length - 1
        ].r_tubing?.interior_diameter_inches || 0
      );
    }
    // Fall back to regular tubing
    return ctx.basicData.value.bottomTubingLengthFt
      ? ctx.basicData.value.bottomTubingInteriorDiameterIn
      : ctx.basicData.value.topTubingInteriorDiameterIn;
  });

  const tubingOuterDiameterAtPumpDischargeIn = computed(() => {
    if (
      _equipment.value?.use_uphole_y_tool &&
      orderedYToolEquipmentTubings.value.length > 0
    ) {
      return (
        orderedYToolEquipmentTubings.value[
          orderedYToolEquipmentTubings.value.length - 1
        ].r_tubing?.outer_diameter_inches || 0
      );
    }
    return _wellProperties.value?.bottom_tubing_length_feet
      ? _wellProperties.value?.r_bottom_tubing?.outer_diameter_inches || 0
      : _wellProperties.value?.r_top_tubing?.outer_diameter_inches || 0;
  });

  // Bypass tubings and order from the form (not equipment) so calcs see live edits
  // const bypassTubingsForStack = computed(() => {
  //   const equip = originalEquipment.value;
  //   const order =
  //     stack === "upper"
  //       ? (equip?.uphole_y_tool_bypass_tubing_order as string[] | undefined) ||
  //         []
  //       : (equip?.downhole_y_tool_bypass_tubing_order as
  //           | string[]
  //           | undefined) || [];
  //   const map = toValue(yToolBypassTubings) as Record<
  //     string,
  //     {
  //       id?: string | null;
  //       length_feet?: number | null;
  //       r_tubing?: { outer_diameter_inches?: number | null } | null;
  //       journal_deleted_at?: unknown;
  //     }
  //   >;
  //   return order
  //     .map(id => map[id])
  //     .filter(
  //       (t): t is NonNullable<typeof t> =>
  //         t != null && !t.journal_deleted_at && t.id != null
  //     )
  //     .map(t => ({
  //       id: t.id as string,
  //       length_feet: t.length_feet,
  //       r_tubing: t.r_tubing,
  //       journal_deleted_at: t.journal_deleted_at
  //     }));
  // });

  // const bypassTubingOrderForStack = computed(() => {
  //   const equip = originalEquipment.value;
  //   return stack === "upper"
  //     ? (equip?.uphole_y_tool_bypass_tubing_order as string[] | undefined) || []
  //     : (equip?.downhole_y_tool_bypass_tubing_order as string[] | undefined) ||
  //         [];
  // });

  // Get bypass tubing OD at motor depth (agnostic of upper/lower)
  const yToolBypassTubingODAtMotorInches = computed(() => {
    let cumulativeDepth = tubingLengthFt.value + yToolLengthFt.value;
    if (!_equipment.value?.use_uphole_y_tool) {
      return 0;
    }
    for (const xrefBypassTubing of orderedYToolBypassTubings.value) {
      const tubing = xrefBypassTubing.r_tubing;

      const tubingLength = xrefBypassTubing.length_feet || 0;
      const tubingEndDepth = cumulativeDepth + tubingLength;

      if (
        bottomOfMotorDepthFt.value >= cumulativeDepth &&
        bottomOfMotorDepthFt.value <= tubingEndDepth
      ) {
        return tubing?.outer_diameter_inches || 0;
      }
      cumulativeDepth = tubingEndDepth;
    }
    return 0;
  });

  // Calculate bypass tubing area at motor
  const yToolBypassTubingAreaAtMotorFt2 = computed(() => {
    return (
      Math.PI *
      Inches.toStandardUnit(yToolBypassTubingODAtMotorInches.value / 2) ** 2
    );
  });

  const shroudIDIn = computed(() => {
    return _equipment.value?.r_shroud?.interior_diameter_inches || 0;
  });

  const sensorCrossSectionFt2 = computed(() => {
    if (!_equipment.value?.r_sensor) {
      return 0;
    }
    const outerDiameterIn =
      shroudIDIn.value > 0
        ? shroudIDIn.value
        : casingInteriorDiameterAtSensorIn.value;
    return getEquipmentCrossSectionFt2(
      outerDiameterIn,
      _equipment.value?.r_sensor?.outer_diameter_inches
    );
  });

  const motorCrossSectionFt2 = computed(() => {
    // Equipment is normalized by stackFiltering before reaching calculations,
    // so r_motor always points to the correct motor for the current stack
    const motorOD = _equipment.value?.r_motor?.outer_diameter_inches || 0;
    // Need this without shroud logic, just the casing
    const outerDiameterIn = getCasingInteriorDiameter(
      _wellProperties.value,
      bottomOfMotorDepthFt.value
    );
    return calculateMotorCrossSectionFt2(
      outerDiameterIn,
      shroudIDIn.value,
      motorOD,
      yToolBypassTubingAreaAtMotorFt2.value
    );
  });

  const sealsCrossSectionFt2 = computed(() => {
    const outerDiameterIn =
      shroudIDIn.value > 0
        ? shroudIDIn.value
        : ctx.casingInteriorDiameterAtSealsIn.value;
    let sealOD = 0;
    if (bottomSeal.value?.r_seal) {
      sealOD = bottomSeal.value.r_seal.outer_diameter_inches;
    } else if (primarySeal.value?.r_seal) {
      sealOD = primarySeal.value.r_seal.outer_diameter_inches;
    }
    return getEquipmentCrossSectionFt2(outerDiameterIn, sealOD);
  });

  const intakeCrossSectionFt2 = computed(() => {
    const outerDiameterIn =
      shroudIDIn.value > 0
        ? shroudIDIn.value
        : casingInteriorDiameterAtIntakeIn.value;
    return getEquipmentCrossSectionFt2(
      outerDiameterIn,
      bottomIntake.value?.r_intake?.outer_diameter_inches
    );
  });

  const topTubingCrossSectionFt2 = computed(() => {
    return getEquipmentCrossSectionFt2(
      ctx.basicData.value.topCasingInteriorDiameterIn,
      _wellProperties.value?.r_top_tubing?.outer_diameter_inches
    );
  });

  const datumPointTVD = computed(() => {
    return extrapolateTVDByMD(ctx.datumPointFt.value, ctx.entryRows.value);
  });

  // motorWeightLbs (weight of motor)

  const topCasingWeightLbs = computed(
    () =>
      _wellProperties.value?.top_casing_length_feet *
      _wellProperties.value?.r_top_casing?.pound_per_foot
  );

  const bottomCasingWeightLbs = computed(
    () =>
      _wellProperties.value?.bottom_casing_length_feet *
      _wellProperties.value?.r_bottom_casing?.pound_per_foot
  );

  const totalCasingWeightLbs = computed(
    () => topCasingWeightLbs.value + bottomCasingWeightLbs.value
  );

  const motorWeightLbs = computed(
    () => _equipment.value?.r_motor?.weight_pounds || 0
  );

  // boltOnDischargeWeightLbs (weight of the bolt-on discharge)
  const boltOnDischargeWeightLbs = computed(
    () => _equipment.value?.r_bolt_on_discharge?.weight_pounds || 0
  );

  // sensorWeightLbs (weight of sensor)
  const sensorWeightLbs = computed(
    () => _equipment.value?.r_sensor?.weight_pounds || 0
  );

  // intakeWeightLbs (add all intake weights together)
  const intakeWeightLbs = computed(() =>
    orderedIntakes.value.reduce(
      (sum, { r_intake }) => sum + r_intake?.weight_pounds,
      0
    )
  );

  // sealWeightLbs (add all seal weights together)
  const sealWeightLbs = computed(() =>
    descendingOrderSeals.value.reduce(
      (sum, { r_seal }) => sum + r_seal?.weight_pounds,
      0
    )
  );

  // pumpWeightLbs (for each pump, add each housing weight together)
  const pumpWeightLbs = computed(() =>
    descendingOrderPumps.value.reduce(
      (sum, pump) => sum + (pump.weight_pounds || 0),
      0
    )
  );

  // mleWeightLbs (for each mle, get the product of the length and weight per foot)
  const mleWeightLbs = computed(() => {
    return (
      _equipment.value?.r_mle?.length_feet *
        _equipment.value?.r_mle?.pound_per_foot || 0
    );
  });

  // tubingWeightLbs (multiples pound_per_foot by the length of the tubing for both top and bottom tubings then adds them together)
  const tubingWeightLbs = computed<number>(
    () =>
      (_wellProperties.value?.r_top_tubing?.pound_per_foot *
        _wellProperties.value?.top_tubing_length_feet || 0) +
      (_wellProperties.value?.r_bottom_tubing?.pound_per_foot *
        _wellProperties.value?.bottom_tubing_length_feet || 0)
  );

  const topTubingWeightLbs = computed<number>(
    () =>
      _wellProperties.value?.r_top_tubing?.pound_per_foot *
        _wellProperties.value?.top_tubing_length_feet || 0
  );

  const bottomTubingWeightLbs = computed<number>(
    () =>
      _wellProperties.value?.r_bottom_tubing?.pound_per_foot *
        _wellProperties.value?.bottom_tubing_length_feet || 0
  );

  const equipmentWeightLbs = computed<number>(
    () =>
      motorWeightLbs.value +
      sensorWeightLbs.value +
      intakeWeightLbs.value +
      sealWeightLbs.value +
      boltOnDischargeWeightLbs.value +
      pumpWeightLbs.value
  );

  const totalStringWeightLbs = computed(() => {
    return (
      equipmentWeightLbs.value +
      tubingWeightLbs.value +
      ctx.totalCablesWeightPounds.value +
      mleWeightLbs.value
    );
  });

  return {
    casingInteriorDiameterAtIntakeIn,
    casingInteriorDiameterAtSensorIn,
    bottomCasingInteriorDiameterIn,
    tubingInternalDiameterAtPumpDischargeIn,
    tubingOuterDiameterAtPumpDischargeIn,
    yToolBypassTubingODAtMotorInches,
    yToolBypassTubingAreaAtMotorFt2,
    shroudIDIn,
    sensorCrossSectionFt2,
    motorCrossSectionFt2,
    sealsCrossSectionFt2,
    intakeCrossSectionFt2,
    topTubingCrossSectionFt2,
    datumPointTVD,
    topCasingWeightLbs,
    bottomCasingWeightLbs,
    totalCasingWeightLbs,
    motorWeightLbs,
    boltOnDischargeWeightLbs,
    sensorWeightLbs,
    intakeWeightLbs,
    sealWeightLbs,
    pumpWeightLbs,
    mleWeightLbs,
    tubingWeightLbs,
    topTubingWeightLbs,
    bottomTubingWeightLbs,
    equipmentWeightLbs,
    totalStringWeightLbs
  };
};

export type DiametersAndWeightsCalcs = ReturnType<
  typeof defineDiametersAndWeights
>;
